import React from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip,
} from 'recharts';
import Card from '../../Card';

// dim_config_hardening 修复点 #8:维度名渲染改多行 + 字体随维度数自适应。
// 见 V4P2Overview.js 同名函数注释。
function formatDimLabel(name, dimCount) {
  let s = (name || '').replace(/^AI\s*/, '');
  s = s.replace(/(能力|意识|度)$/, '').replace(/与/g, '');
  const fontSize = dimCount >= 9 ? 9 : 10;
  const maxCharsPerLine = dimCount >= 9 ? 3 : 4;
  if (s.length <= maxCharsPerLine) return { lines: [s], fontSize };
  return {
    lines: [
      s.slice(0, maxCharsPerLine),
      s.slice(maxCharsPerLine, maxCharsPerLine * 2),
    ].filter(Boolean),
    fontSize,
  };
}

function dimBarColor(score, notTested) {
  if (notTested) return '#94a3b8';
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#2563eb';
  return '#d97706';
}

function makePolarTickRenderer(dimCount) {
  return function PolarTick({ payload, x, y, textAnchor }) {
    const { lines, fontSize } = formatDimLabel(payload.value, dimCount);
    return (
      <text x={x} y={y} textAnchor={textAnchor} fill="#64748b" fontSize={fontSize}>
        {lines.map((line, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 0 : fontSize + 2}>{line}</tspan>
        ))}
      </text>
    );
  };
}

export default function P2Profile({ report }) {
  const dims = report?.core?.dimension_scores || [];
  const insights = report?.behavior_insights;
  const hasInsights = !!insights && Object.keys(insights).length > 0;
  const title = hasInsights ? '能力画像 + 行为洞察' : '能力画像';

  const radarData = dims
    .filter(d => !d.not_tested)
    .map(d => ({ subject: d.dimension_name, value: d.score, fullMark: 100 }));
  const renderTick = makePolarTickRenderer(radarData.length);

  const riskSignals = Array.isArray(insights?.risk_signals) ? insights.risk_signals : [];

  return (
    <div data-testid="report-page-p2">
      <Card style={{ padding: '28px 32px', marginBottom: 24 }}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 18 }}>{title}</h2>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {radarData.length >= 3 && (
            <div data-testid="report-page-p2-radar" style={{ flex: '1 1 280px', minWidth: 0, maxWidth: 400, width: '100%', height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  data={radarData}
                  outerRadius={radarData.length >= 9 ? '55%' : '60%'}
                  margin={{ top: 20, right: 32, bottom: 20, left: 32 }}
                >
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="subject" tick={renderTick} />
                  <Radar name="得分" dataKey="value" stroke="#1a3a6b" fill="#1a3a6b" fillOpacity={0.25} />
                  <Tooltip formatter={(v) => [`${v} 分`, '得分']} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div data-testid="report-page-p2-dim-bars" style={{ flex: '1 1 240px', minWidth: 0 }}>
            {dims.map(d => (
              <div key={d.dimension_id} style={{ marginBottom: 16, opacity: d.not_tested ? 0.6 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {d.dimension_name}
                    {d.not_tested && (
                      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#94a3b8', background: '#e2e8f0', padding: '1px 6px', borderRadius: 3, whiteSpace: 'nowrap' }}>
                        未测试
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: d.not_tested ? '#94a3b8' : 'var(--color-primary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {d.not_tested ? '—' : `${d.score} 分`}
                  </span>
                </div>
                <div style={{ height: 6, background: 'var(--color-border)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: d.not_tested ? '0%' : `${d.score ?? 0}%`,
                    background: dimBarColor(d.score, d.not_tested),
                    borderRadius: 3, transition: 'width 0.6s ease',
                  }} />
                </div>
                {d.dimension_description && (
                  <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 3, lineHeight: 1.5 }}>
                    {d.dimension_description}
                  </p>
                )}
                {d.special_count > 0 && (
                  <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                    含 {d.special_count} 题特殊选项
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {hasInsights && (
        <Card data-testid="report-page-p2-insights" style={{ padding: '24px 28px', marginBottom: 24, borderTop: '3px solid #7c3aed' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#7c3aed' }}>行为洞察</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 12 }}>
            {insights.confidence_level && (
              <div>
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>信心度</p>
                <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{insights.confidence_level}</p>
                {insights.confidence_desc && (
                  <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{insights.confidence_desc}</p>
                )}
              </div>
            )}
            {insights.strategy_type && (
              <div>
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>答题策略</p>
                <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{insights.strategy_type}</p>
                {insights.strategy_desc && (
                  <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{insights.strategy_desc}</p>
                )}
              </div>
            )}
          </div>
          {insights.special_details && (
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
              {insights.special_details}
            </p>
          )}
          {riskSignals.length > 0 && (
            <ul style={{ margin: '12px 0 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {riskSignals.map((sig, i) => (
                <li key={i} style={{ fontSize: 13, color: '#b91c1c', lineHeight: 1.6 }}>{sig}</li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
